export const WORKSPACE_STORAGE_KEY = 'anvil.workspace.v1';
export const WORKSPACE_VERSION = 1;
export const PANEL_HEADER_HEIGHT = 34;

export const DEFAULT_PANELS = Object.freeze({
  outliner: { mode: 'dock', edge: 'left', lane: 0, width: 232, height: 420 },
  tools: { mode: 'dock', edge: 'left', lane: 1, width: 48, height: 300 },
  inspector: { mode: 'dock', edge: 'right', lane: 0, width: 280, height: 520 },
  add: { mode: 'closed', x: 310, y: 82, width: 260, height: 360 },
  debug: { mode: 'closed', x: 550, y: 48, width: 330, height: 420 },
});

const MODES = new Set(['dock', 'float', 'closed']);
const EDGES = new Set(['left', 'right', 'bottom']);
const OPEN_MODES = new Set(['dock', 'float']);

function number(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

function bounds(viewport) {
  const x = number(viewport?.x, 0);
  const hasUsableOrigin = Number.isFinite(viewport?.y);
  const y = number(viewport?.y, 30);
  const width = Math.max(1, number(viewport?.width, 1));
  const windowHeight = number(viewport?.height, 1);
  const height = Math.max(1, hasUsableOrigin ? windowHeight : windowHeight - y - 34);
  return { x, y, width, height };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

export function fitSideLaneWidths(
  left,
  right,
  usableWidth,
  minLaneWidth = 48,
  minViewportWidth = 160,
) {
  const normalise = (widths) => widths.map((width) => Math.max(minLaneWidth, number(width, minLaneWidth)));
  const desiredLeft = normalise(left);
  const desiredRight = normalise(right);
  const desired = [...desiredLeft, ...desiredRight];
  const dockBudget = Math.max(0, usableWidth - minViewportWidth);
  const minimumTotal = desired.length * minLaneWidth;
  if (minimumTotal > dockBudget) {
    return { mode: 'viewport-only', left: [], right: [], viewportWidth: usableWidth };
  }

  const desiredTotal = desired.reduce((sum, width) => sum + width, 0);
  const fit = (widths) => {
    if (desiredTotal <= dockBudget) return widths;
    const desiredExtra = desiredTotal - minimumTotal;
    const availableExtra = dockBudget - minimumTotal;
    return widths.map((width) => minLaneWidth +
      (desiredExtra === 0 ? 0 : ((width - minLaneWidth) / desiredExtra) * availableExtra));
  };
  const fittedLeft = fit(desiredLeft);
  const fittedRight = fit(desiredRight);
  const used = [...fittedLeft, ...fittedRight].reduce((sum, width) => sum + width, 0);
  return { mode: 'fit', left: fittedLeft, right: fittedRight, viewportWidth: usableWidth - used };
}

export function clampFloatForRender(state, viewport) {
  const usable = bounds(viewport);
  const width = Math.min(Math.max(48, number(state?.width, 48)), usable.width);
  const height = Math.min(Math.max(PANEL_HEADER_HEIGHT, number(state?.height, PANEL_HEADER_HEIGHT)), usable.height);
  return {
    ...state,
    x: clamp(number(state?.x, usable.x), usable.x, usable.x + usable.width - width),
    y: clamp(
      number(state?.y, usable.y),
      usable.y,
      usable.y + usable.height - PANEL_HEADER_HEIGHT,
    ),
    width,
    height,
  };
}

export function dockPanelAdjacent(panelStates, id, targetId, side, dimensions = {}) {
  const next = structuredClone(panelStates);
  const target = next[targetId];
  if (!target || target.mode !== 'dock') return next;
  const previous = next[id] ?? {};
  const size = {
    width: number(dimensions.width, number(previous.width, target.width)),
    height: number(dimensions.height, number(previous.height, target.height)),
  };
  const lane = target.lane ?? 0;
  const stackSides = target.edge === 'bottom'
    ? new Set(['left', 'right'])
    : new Set(['top', 'bottom']);

  if (stackSides.has(side)) {
    const stack = Object.entries(next)
      .filter(([candidateId, state]) => candidateId !== id && state?.mode === 'dock' &&
        state.edge === target.edge && (state.lane ?? 0) === lane)
      .sort(([, a], [, b]) => number(a.order, 0) - number(b.order, 0));
    const targetIndex = stack.findIndex(([candidateId]) => candidateId === targetId);
    const before = target.edge === 'bottom' ? side === 'left' : side === 'top';
    stack.splice(Math.max(0, targetIndex + (before ? 0 : 1)), 0, [id, {
      mode: 'dock', edge: target.edge, lane, order: 0, ...size,
    }]);
    stack.forEach(([candidateId, state], order) => {
      next[candidateId] = { ...state, order };
    });
    return next;
  }

  const before = target.edge === 'left'
    ? side === 'left'
    : target.edge === 'right'
      ? side === 'right'
      : side === 'bottom';
  const insertedLane = lane + (before ? 0 : 1);
  for (const [candidateId, state] of Object.entries(next)) {
    if (candidateId === id || state?.mode !== 'dock' || state.edge !== target.edge) continue;
    if ((state.lane ?? 0) >= insertedLane) next[candidateId] = { ...state, lane: (state.lane ?? 0) + 1 };
  }
  next[id] = { mode: 'dock', edge: target.edge, lane: insertedLane, order: 0, ...size };
  return next;
}

export function fitDockStackSizes(desiredSizes, availableSize, minSize = PANEL_HEADER_HEIGHT) {
  if (desiredSizes.length === 0) return [];
  if (availableSize < desiredSizes.length * minSize) {
    return desiredSizes.map(() => Math.max(0, availableSize / desiredSizes.length));
  }
  const weights = desiredSizes.map((size) => Math.max(0, number(size, minSize)));
  const totalWeight = weights.reduce((sum, size) => sum + size, 0);
  const flexible = availableSize - desiredSizes.length * minSize;
  return weights.map((weight) => minSize + flexible *
    (totalWeight === 0 ? 1 / weights.length : weight / totalWeight));
}

/**
 * Fit bottom dock rows into the usable height without asking CSS to render a
 * panel below its minimum. If stacked minimums cannot leave the required upper
 * region, all bottom panels share one horizontal row. If that row still leaves
 * less than the required upper region, `combined-row` tells the DOM layer to
 * place side docks in the same row. Side-only layouts also use the complete
 * same-lane stack height as their hard minimum. If the required dock geometry
 * cannot fit, `viewport-only` temporarily suppresses docks rather than
 * violating CSS or covering the view.
 */
export function fitBottomLaneHeights(
  desiredHeights,
  usableHeight,
  minPanelHeight = PANEL_HEADER_HEIGHT,
  preferredViewportHeight = PANEL_HEADER_HEIGHT,
  requiredUpperHeight = 1,
) {
  const desired = desiredHeights.map((height) => Math.max(minPanelHeight, number(height, minPanelHeight)));
  const hasBottomDocks = desired.length > 0;
  const hasSideDocks = requiredUpperHeight >= minPanelHeight;
  const bottomDockBlocksViewport = hasBottomDocks && usableHeight <= minPanelHeight;
  const sideDockCannotFit = !hasBottomDocks && hasSideDocks && usableHeight < requiredUpperHeight;
  if (bottomDockBlocksViewport || sideDockCannotFit) {
    return { mode: 'viewport-only', heights: [], viewportHeight: usableHeight };
  }
  if (!hasBottomDocks) return { mode: 'stack', heights: [], viewportHeight: usableHeight };

  const minimumStackHeight = desired.length * minPanelHeight;
  const maximumStackHeight = Math.max(0, usableHeight - requiredUpperHeight);
  if (minimumStackHeight > maximumStackHeight) {
    const rowHeight = Math.min(
      Math.max(...desired),
      Math.max(minPanelHeight, usableHeight - Math.max(preferredViewportHeight, requiredUpperHeight)),
    );
    const viewportHeight = Math.max(0, usableHeight - rowHeight);
    return {
      mode: requiredUpperHeight >= minPanelHeight && viewportHeight < requiredUpperHeight
        ? 'combined-row'
        : 'row',
      heights: [rowHeight],
      viewportHeight,
    };
  }

  const desiredTotal = desired.reduce((sum, height) => sum + height, 0);
  const laneBudget = Math.min(
    desiredTotal,
    maximumStackHeight,
    Math.max(minimumStackHeight, usableHeight - preferredViewportHeight),
  );
  let heights = desired;
  if (desiredTotal > laneBudget) {
    const desiredExtra = desiredTotal - minimumStackHeight;
    const availableExtra = laneBudget - minimumStackHeight;
    heights = desired.map((height) =>
      minPanelHeight + (desiredExtra === 0 ? 0 : ((height - minPanelHeight) / desiredExtra) * availableExtra));
  }
  const used = heights.reduce((sum, height) => sum + height, 0);
  return { mode: 'stack', heights, viewportHeight: Math.max(0, usableHeight - used) };
}

function normalisePanel(value, fallback, viewport) {
  const mode = MODES.has(value?.mode) ? value.mode : fallback.mode;
  const openMode = mode === 'closed'
    ? OPEN_MODES.has(value?.openMode)
      ? value.openMode
      : EDGES.has(value?.edge) || fallback.edge ? 'dock' : 'float'
    : null;
  const desiredWidth = number(value?.width, fallback.width);
  const width = mode === 'closed'
    ? Math.max(48, desiredWidth)
    : clamp(desiredWidth, 48, viewport.width);
  const desiredHeight = number(value?.height, fallback.height);
  const height = mode === 'dock' || mode === 'closed'
    ? Math.max(PANEL_HEADER_HEIGHT, desiredHeight)
    : clamp(desiredHeight, PANEL_HEADER_HEIGHT, viewport.height);
  const panel = { mode, width, height };

  if (mode === 'dock') {
    panel.edge = EDGES.has(value?.edge) ? value.edge : fallback.edge ?? 'left';
    panel.lane = Math.max(0, Math.trunc(number(value?.lane, fallback.lane ?? 0)));
    panel.order = Math.max(0, Math.trunc(number(value?.order, fallback.order ?? 0)));
    return panel;
  }

  const fallbackX = number(fallback.x, viewport.x);
  const fallbackY = number(fallback.y, viewport.y);
  if (mode === 'closed') {
    panel.x = number(value?.x, fallbackX);
    panel.y = number(value?.y, fallbackY);
    panel.openMode = openMode;
    if (EDGES.has(value?.edge)) panel.edge = value.edge;
    if (Number.isFinite(value?.lane)) panel.lane = Math.max(0, Math.trunc(value.lane));
    if (Number.isFinite(value?.order)) panel.order = Math.max(0, Math.trunc(value.order));
    return panel;
  }
  panel.x = clamp(number(value?.x, fallbackX), viewport.x, viewport.x + viewport.width - width);
  panel.y = clamp(
    number(value?.y, fallbackY),
    viewport.y,
    viewport.y + viewport.height - PANEL_HEADER_HEIGHT,
  );
  return panel;
}

/**
 * Validate persisted workspace state and make every panel recoverable.
 * `viewport` is the usable rectangle after fixed application chrome.
 */
export function normaliseWorkspace(saved, viewport) {
  const usable = bounds(viewport);
  const source = saved?.version === WORKSPACE_VERSION && saved.panels ? saved.panels : {};
  const panels = {};

  for (const [id, fallback] of Object.entries(DEFAULT_PANELS)) {
    const positionedFallback = id === 'debug' && !source[id]
      ? { ...fallback, x: usable.x + usable.width - Math.min(fallback.width, usable.width) - 12 }
      : fallback;
    panels[id] = normalisePanel(source[id], positionedFallback, usable);
  }

  return { version: WORKSPACE_VERSION, panels };
}

function distanceToEdges(rect, viewport) {
  return [
    { distance: Math.abs(rect.x - viewport.x), snap: { kind: 'edge', edge: 'left' } },
    {
      distance: Math.abs(viewport.x + viewport.width - (rect.x + rect.width)),
      snap: { kind: 'edge', edge: 'right' },
    },
    {
      distance: Math.abs(viewport.y + viewport.height - (rect.y + rect.height)),
      snap: { kind: 'edge', edge: 'bottom' },
    },
  ];
}

function distanceToPanel(rect, candidate) {
  const overlapsVertically = rect.y < candidate.y + candidate.height && rect.y + rect.height > candidate.y;
  const overlapsHorizontally = rect.x < candidate.x + candidate.width && rect.x + rect.width > candidate.x;
  return [
    {
      distance: Math.abs(rect.x + rect.width - candidate.x),
      snap: { kind: 'panel', id: candidate.id, side: 'left' },
    },
    {
      distance: Math.abs(rect.x - (candidate.x + candidate.width)),
      snap: { kind: 'panel', id: candidate.id, side: 'right' },
    },
    {
      distance: Math.abs(rect.y + rect.height - candidate.y),
      snap: { kind: 'panel', id: candidate.id, side: 'top' },
    },
    {
      distance: Math.abs(rect.y - (candidate.y + candidate.height)),
      snap: { kind: 'panel', id: candidate.id, side: 'bottom' },
    },
  ].filter(({ snap }) => ['left', 'right'].includes(snap.side) ? overlapsVertically : overlapsHorizontally);
}

/** Find the closest dock target for a floating rectangle. */
export function findSnap(rect, candidates, viewport, threshold = 16) {
  const usable = bounds(viewport);
  let best = null;

  // Edges are considered first, so they win equal-distance ties.
  for (const target of distanceToEdges(rect, usable)) {
    if (target.distance <= threshold && (!best || target.distance < best.distance)) best = target;
  }

  for (const candidate of candidates ?? []) {
    if (!candidate || candidate.id == null) continue;
    for (const target of distanceToPanel(rect, candidate)) {
      if (target.distance <= threshold && (!best || target.distance < best.distance)) best = target;
    }
  }

  return best?.snap ?? null;
}
